import { useIntl } from 'react-intl'
import InputField from '../InputField'
import styled from 'styled-components'
import { FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, Tooltip } from '@mui/material'
import SearchButton from '../SearchButton'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { getProviderCourses } from 'store/course/thunks'
import { useSelector } from 'react-redux'
import { selectCourse } from 'store/course/slice'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import { useAppDispatch } from 'store'
import { sendErrorNotification } from 'utils/notifications'
import CourseProviderSearchClubs from './CourseProviderSearchClubs'
import { FormattedMessageWrapper } from 'components/ui/FormattedMessageWrapper'

enum FEDERATION_PROVIDER_ID {
  SGL = '3',
  MGB = '2',
  RFEG = '8',
}

export default function AddProviderMapping() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [showClubs, setShowClubs] = useState<boolean>(false)
  const { getProviderCoursesStatus, course } = useSelector(selectCourse)
  const [providerChecked, setProviderChecked] = useState<string>(FEDERATION_PROVIDER_ID.SGL)
  const { handleSubmit, register, watch } = useForm<ProviderSearchForm>()
  const [searchResult, setSearchResult] = useState<ProviderSearchClubs[]>()
  const searchCharacters = watch('clubName')

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProviderChecked((event.target as HTMLInputElement).value)
  }

  const handleSearch = async (data: ProviderSearchForm) => {
    setShowClubs(false)
    try {
      const res = await dispatch(
        getProviderCourses({
          searchString: data.clubName,
          providerId: providerChecked,
        }),
      ).unwrap()
      if (!res.data.length) {
        sendErrorNotification(`No clubs found with ${data.clubName}`)
        return
      }
      setSearchResult(res.data)
      setShowClubs(true)
    } catch (error) {
      setShowClubs(false)
      console.error(error)
      sendErrorNotification(`Provider clubs with search string ${data.clubName} not found`)
    }
  }

  if (getProviderCoursesStatus === 'loading') {
    return (
      <LoaderContainer>
        <LoadingSpinnerGreen size={92} />
      </LoaderContainer>
    )
  }

  const renderTooltip = () => {
    return (
      <Tooltip title="info">
        <TooltipContent>
          <FormattedMessageWrapper id="strings.courseNeedsOfficialVersionForMapping" />
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <TransferForm>
      <SearchForm onSubmit={handleSubmit(handleSearch)}>
        <FiltersSection item xs={4}>
          <StyledInputField
            name="clubName"
            type="text"
            register={register}
            label={intl.formatMessage({ id: 'strings.clubsName' })}
            outlined
          />
          <FormControl sx={{ display: 'flex', margin: '5' }}>
            <FormLabel id="provider-button-group">Providers:</FormLabel>
            <RadioGroup
              aria-labelledby="provider-button-group"
              name="provider-button-group"
              value={providerChecked}
              onChange={handleChange}
            >
              {Object.keys(FEDERATION_PROVIDER_ID).map((federation) => {
                return (
                  <FormControlLabel
                    key={federation}
                    value={FEDERATION_PROVIDER_ID[federation]}
                    control={<Radio />}
                    label={federation}
                  />
                )
              })}
            </RadioGroup>
          </FormControl>
        </FiltersSection>
        <ActionButton
          type="submit"
          text={intl.formatMessage({ id: 'strings.search' })}
          disabled={!searchCharacters || searchCharacters.length < 3 || !course.isOfficial}
        />
        {!course.isOfficial && renderTooltip()}
        <CourseProviderSearchClubs searchResult={searchResult} showClubs={showClubs} />
      </SearchForm>
    </TransferForm>
  )
}

const SearchForm = styled.form`
  & .MuiInputLabel-root {
    color: #404040;
    padding: 0;
  }
  & .MuiInputBase-formControl {
    background-color: ${(props) => props.theme.customPalette.lightGray};
    min-height: 40px;
    padding: 5px;
    & .MuiInputBase-input {
      padding: 0 0 0 5px;
    }
  }
  width: 100%;
  display: flex;
  flex-direction: column;
`

const StyledInputField = styled(InputField)`
  margin: 5px 0 30px;
  width: 60%;
`

const FiltersSection = styled(Grid)`
  margin: 20px 0;
`

const ActionButton = styled(SearchButton)`
  align-self: flex-end;
`

const TransferForm = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  & > span {
    color: ${(props) => props.theme.palette.primary.main};
    width: 100%;
  }
  & > input {
    height: 30px;
    margin-top: 10px;
    padding: 0 10px;

    &::placeholder {
      color: ${(props) => props.theme.customPalette.mediumGray};
    }
  }
  & > button {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.customPalette.light};
    height: 35px;
    border: 1px solid;
    border-color: ${(props) => props.theme.palette.primary.main};
    border-radius: 5px;
    margin-top: 10px;
    cursor: pointer;
    margin-left: 5px;

    &:not(.disabled):hover {
      opacity: 0.85;
    }

    &.disabled {
      background-color: ${(props) => props.theme.customPalette.mediumGray};
      border-color: ${(props) => props.theme.customPalette.mediumGray};
      cursor: unset;
    }
  }
`

const LoaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 50px 0;
`

const TooltipContent = styled.div`
  font-style: normal;
  font-size: 17px;
  line-height: 150%;
  color: red;
`
